import { useContext, useMemo } from 'react'
import DepartureSection from './price-overview/departure-section'
import { DepartureContext } from '../contexts/departure'
import { useTranslation } from 'react-i18next'
import CabinSection from './price-overview/cabin-section'
import ProductSection from './price-overview/product-section'
import { PassengersContext } from '../contexts/passengers'
import VoucherSection from './price-overview/voucher-section'
import { CabinsContext } from '../contexts/cabins'
import {
  useCabinPricing,
  useFullCabinSelection,
  useNumberOfPassengers
} from '../hooks/cabin-selection'
import { VoucherContext } from '../contexts/voucher'
import { useDiscount } from '../hooks/vouchers'
import { useCurrencyFormatter } from '../hooks/formatter'
import { useAdditionalProducts, usePassengerProducts } from '../hooks/products'
import OverviewBlock from './price-overview/block'
import SGRSection from './price-overview/sgr-section'
import { ProductsContext } from '../contexts/products'
import { hasSGR, hasVAT } from '../utils/products'

export default function PriceOverview ({ additionalProducts }: { additionalProducts: Record<string, number> }): JSX.Element {
  const { departure } = useContext(DepartureContext)
  const { t } = useTranslation()
  const formatter = useCurrencyFormatter()
  const { validated } = useContext(PassengersContext)

  const { cabinTypeIds = [] } = useContext(CabinsContext)
  const cabinSelection = useFullCabinSelection(cabinTypeIds)

  const { totalCabinPrice, totalPassengers } = useCabinPricing({ cabinSelection })
  const { voucher } = useContext(VoucherContext)
  const discount = useDiscount(totalCabinPrice, totalPassengers, voucher)

  const { passengerProductsTotalPrice } = usePassengerProducts({
    passengerInformation: validated ?? { passengers: [] },
    bikes: true,
    diets: true,
    other: true,
    days: departure.duration
  })
  const { additionalProductsTotalPrice } = useAdditionalProducts(additionalProducts ?? {}, departure.duration)
  const numberOfPassengers = useNumberOfPassengers(cabinSelection)
  const { products } = useContext(ProductsContext)

  const sgr = hasSGR(departure, validated)
  const sgrCost = sgr ? products.sgr.unit_price * numberOfPassengers : 0

  const totalPrice = useMemo(
    () => additionalProductsTotalPrice + sgrCost + passengerProductsTotalPrice + totalCabinPrice - discount,
    [additionalProductsTotalPrice, discount, passengerProductsTotalPrice, sgrCost, totalCabinPrice])

  const vat = hasVAT(departure, validated)
  const vatAmount = useMemo(() => totalPrice - totalPrice / 1.09, [totalPrice])

  return (
    <>
      <h2 className='text-brand-primary text-lg font-bold mb-4'>{t('price_overview', 'Price overview')}</h2>
      <section className='bg-brand-well mb-8'>
        <DepartureSection departure={departure} />
        <CabinSection />
        {validated !== undefined && <ProductSection passengerInformation={validated} additionalProducts={additionalProducts} />}
        <VoucherSection />
        {sgr && <SGRSection numberOfPassengers={numberOfPassengers} sgrProduct={products.sgr} />}
        <OverviewBlock className='text-xl' label={t('total', 'Total')} value={<span className='text-black'>{formatter.format(totalPrice)}</span>} />
        {vat && <OverviewBlock className='text-sm' label={t('vat', 'VAT (9%)')} value={formatter.format(vatAmount)} />}
      </section>
    </>
  )
}
